import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, ImagePlus, Loader2, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { TagPicker } from "@/components/marketplace/tag-picker";

import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { fetchMarketplaceCategories } from "@/lib/marketplace/queries";
import { getSuggestions } from "@/lib/marketplace/suggestions";
import { uploadImage } from "@/lib/uploads";
import { ImageCropDialog } from "@/components/image-crop-dialog";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/services/new")({
  head: () => ({
    meta: [
      { title: "Create a service | BeHot.Now Marketplace" },
      { name: "description", content: "Publish a service on the BeHot.Now Marketplace in a few guided steps." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: NewServicePage,
});

type PackageDraft = {
  tier: "basic" | "standard" | "premium";
  title: string;
  description: string;
  priceUsd: string;
  deliveryDays: string;
  revisions: string;
  features: string;
};

const emptyPackage = (tier: PackageDraft["tier"]): PackageDraft => ({
  tier,
  title: "",
  description: "",
  priceUsd: "",
  deliveryDays: "3",
  revisions: "1",
  features: "",
});

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

const STEP_KEYS = ["mpsell.new.step.category","mpsell.new.step.overview","mpsell.new.step.packages","mpsell.new.step.media","mpsell.new.step.requirements"] as const;

function NewServicePage() {
  const { t } = useT();
  const STEPS = STEP_KEYS.map((k) => t(k));
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  const { data: application } = useQuery({
    queryKey: ["mp-seller-application", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data } = await supabase
        .from("marketplace_seller_applications")
        .select("application_status")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data;
    },
  });

  const { data: categories } = useQuery({ queryKey: ["mp-categories"], queryFn: fetchMarketplaceCategories });

  const [step, setStep] = useState(0);
  const [parentId, setParentId] = useState("");
  const [subId, setSubId] = useState("");
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  
  const [coverUrl, setCoverUrl] = useState("");
  const [gallery, setGallery] = useState<string[]>([]);
  const [uploading, setUploading] = useState<"cover" | "gallery" | null>(null);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropKind, setCropKind] = useState<"cover" | "gallery">("cover");
  const [cropOpen, setCropOpen] = useState(false);
  const [packages, setPackages] = useState<PackageDraft[]>([emptyPackage("basic")]);
  const [requirements, setRequirements] = useState<string[]>([""]);

  const parents = useMemo(() => (categories ?? []).filter((c) => !c.parent_id), [categories]);
  const subs = useMemo(
    () => (categories ?? []).filter((c) => c.parent_id === parentId),
    [categories, parentId],
  );
  const parentSlug = parents.find((c) => c.id === parentId)?.slug ?? null;
  const subSlug = subs.find((c) => c.id === subId)?.slug ?? null;
  const suggestions = useMemo(() => getSuggestions(subSlug, parentSlug), [subSlug, parentSlug]);

  const isApproved = application?.application_status === "seller_approved";


  const pickImage = (files: FileList | null, kind: "cover" | "gallery") => {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("mpsell.new.pleaseChooseImage"));
      return;
    }
    setCropKind(kind);
    setCropFile(file);
    setCropOpen(true);
  };

  const handleUpload = async (file: File, kind: "cover" | "gallery") => {
    if (!user) return;
    setUploading(kind);
    const localPreview = URL.createObjectURL(file);
    try {
      if (kind === "cover") {
        const url = await uploadImage(file, user.id, "service-cover");
        setPreviews((p) => ({ ...p, [url]: localPreview }));
        setCoverUrl(url);
      } else {
        if (gallery.length >= 6) return;
        const url = await uploadImage(file, user.id, "service-gallery");
        setPreviews((p) => ({ ...p, [url]: localPreview }));
        setGallery((prev) => [...prev, url]);
      }
    } catch (e) {
      URL.revokeObjectURL(localPreview);
      toast.error(e instanceof Error ? e.message : t("mpsell.new.uploadFailed"));
    } finally {
      setUploading(null);
    }
  };

  const create = useMutation({
    mutationFn: async (publish: boolean) => {
      if (!user) throw new Error(t("mpsell.new.signInRequired"));
      const basic = packages.find((p) => p.tier === "basic");
      if (!basic?.priceUsd) throw new Error(t("mpsell.new.basicPriceRequired"));
      const slug = `${slugify(title)}-${Math.random().toString(36).slice(2, 7)}`;

      const { data: service, error } = await supabase
        .from("marketplace_services")
        .insert({
          seller_user_id: user.id,
          title,
          slug,
          category_id: parentId,
          subcategory_id: subId || null,
          short_description: shortDescription || null,
          description,
          tags,
          cover_url: coverUrl || null,
          gallery,
          starting_price_cents: Math.round(Number(basic.priceUsd) * 100),
          currency: "USD",
          delivery_days: Math.min(...packages.map((p) => Number(p.deliveryDays) || 1)),
          status: publish ? "pending_moderation" : "draft",
        })
        .select("id")
        .single();
      if (error) throw error;

      const pkgRows = packages
        .filter((p) => p.title && p.priceUsd)
        .map((p, i) => ({
          service_id: service.id,
          tier: p.tier,
          name: p.title,
          description: p.description,
          price_cents: Math.round(Number(p.priceUsd) * 100),
          delivery_days: Math.max(1, Number(p.deliveryDays) || 1),
          revisions: Number(p.revisions) || 0,
          deliverables_json: p.features.split("\n").map((f) => f.trim()).filter(Boolean),
          sort_order: i,
        }));
      if (pkgRows.length) {
        const { error: pkgErr } = await supabase.from("marketplace_service_packages").insert(pkgRows);
        if (pkgErr) throw pkgErr;
      }

      const reqRows = requirements
        .map((r, i) => ({ service_id: service.id, prompt: r.trim(), sort_order: i }))
        .filter((r) => r.prompt);
      if (reqRows.length) {
        const { error: reqErr } = await supabase.from("marketplace_service_requirements").insert(reqRows);
        if (reqErr) throw reqErr;
      }
      return service.id;
    },
    onSuccess: (_id, publish) => {
      toast.success(publish ? t("mpsell.new.submitSuccess") : t("mpsell.new.draftSaved"));
      void navigate({ to: "/marketplace/sell" });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("mpsell.new.saveError")),
  });

  if (!user) return null;

  if (application && !isApproved) {
    return (
      <MarketplaceGate>
        <div className="mx-auto max-w-xl px-4 py-16 text-center">
          <h1 className="font-display text-2xl font-bold">{t("mpsell.new.approvalRequiredTitle")}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {t("mpsell.new.approvalRequiredBody")}
          </p>
          <Button asChild variant="hot" className="mt-6">
            <a href="/marketplace/sell">{t("mpsell.new.goToApplication")}</a>
          </Button>
        </div>
      </MarketplaceGate>
    );
  }

  const updatePackage = (i: number, patch: Partial<PackageDraft>) =>
    setPackages((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const canContinue = () => {
    if (step === 0) return Boolean(parentId && subId);
    if (step === 1) return Boolean(title.trim().length > 10 && description.trim().length > 30);
    if (step === 2) return packages.every((p) => p.title && p.priceUsd);
    return true;
  };

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="font-display text-3xl font-bold">{t("mpsell.new.pageTitle")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("mpsell.new.pageSubtitle")}
        </p>

        {/* Stepper */}
        <ol className="mt-6 flex flex-wrap items-center gap-2">
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => i < step && setStep(i)}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  i === step
                    ? "border-primary bg-primary/10 text-primary"
                    : i < step
                      ? "border-border text-muted-foreground hover:text-foreground"
                      : "border-border/60 text-muted-foreground/60"
                }`}
              >
                <span className="flex size-5 items-center justify-center rounded-full bg-muted text-[10px]">
                  {i < step ? <Check className="size-3" /> : i + 1}
                </span>
                {s}
              </button>
            </li>
          ))}
        </ol>

        {/* Step 1 — Category */}
        {step === 0 && (
          <div className="card-surface mt-6 space-y-5 p-6">
            <div>
              <Label>{t("mpsell.new.mainCategory")}</Label>
              <Select
                value={parentId}
                onValueChange={(v) => {
                  setParentId(v);
                  setSubId("");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("mpsell.new.chooseCategory")} />
                </SelectTrigger>
                <SelectContent>
                  {parents.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("mpsell.new.subcategory")}</Label>
              <Select value={subId} onValueChange={setSubId} disabled={!parentId}>
                <SelectTrigger>
                  <SelectValue placeholder={parentId ? t("mpsell.new.chooseSubcategory") : t("mpsell.new.pickMainCategoryFirst")} />
                </SelectTrigger>
                <SelectContent>
                  {subs.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-muted-foreground">
                {t("mpsell.new.notSureHint")}
              </p>
            </div>
          </div>
        )}

        {/* Step 2 — Overview */}
        {step === 1 && (
          <div className="card-surface mt-6 space-y-5 p-6">
            <div>
              <Label htmlFor="svc-title">{t("mpsell.new.title")}</Label>
              <Input
                id="svc-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={suggestions.titles[0]}
                maxLength={90}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {suggestions.titles.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTitle(t)}
                    className="rounded-full border border-border px-3 py-1 text-left text-[11px] text-muted-foreground hover:border-primary hover:text-primary"
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="svc-short">{t("mpsell.new.shortDescription")}</Label>
              <Input
                id="svc-short"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder={t("mpsell.new.shortDescPlaceholder")}
                maxLength={140}
              />
            </div>
            <div>
              <Label htmlFor="svc-desc">{t("mpsell.new.description")}</Label>
              <Textarea
                id="svc-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={7}
                placeholder={suggestions.descriptionHint}
              />
              <p className="mt-1 text-xs text-muted-foreground">{suggestions.descriptionHint}</p>
            </div>
            <TagPicker
              value={tags}
              onChange={setTags}
              parentSlug={parentSlug}
              recommended={suggestions.tags}
            />

          </div>
        )}

        {/* Step 3 — Packages */}
        {step === 2 && (
          <div className="mt-6 space-y-4">
            <p className="text-xs text-muted-foreground">
              {t("mpsell.new.ideasForCategory", { ideas: suggestions.packageIdeas.join(" · ") })}
            </p>
            {packages.map((p, i) => (
              <div key={p.tier} className="card-surface space-y-4 p-5">
                <div className="flex items-center justify-between">
                  <p className="font-display text-sm font-bold uppercase tracking-wide text-primary">{p.tier}</p>
                  {p.tier !== "basic" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setPackages((prev) => prev.filter((x) => x.tier !== p.tier))}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>{t("mpsell.new.packageTitle")}</Label>
                    <Input
                      value={p.title}
                      placeholder={suggestions.packageIdeas[i] ?? t("mpsell.new.packageNamePlaceholder")}
                      onChange={(e) => updatePackage(i, { title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("mpsell.new.priceUsd")}</Label>
                    <Input
                      type="number"
                      min="5"
                      value={p.priceUsd}
                      onChange={(e) => updatePackage(i, { priceUsd: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("mpsell.new.deliveryDays")}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={p.deliveryDays}
                      onChange={(e) => updatePackage(i, { deliveryDays: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("mpsell.new.revisionsIncluded")}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={p.revisions}
                      onChange={(e) => updatePackage(i, { revisions: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>{t("mpsell.new.whatsIncluded")}</Label>
                  <Textarea
                    value={p.features}
                    onChange={(e) => updatePackage(i, { features: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
            ))}
            {packages.length < 3 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const next = packages.length === 1 ? "standard" : "premium";
                  setPackages((prev) => [...prev, emptyPackage(next)]);
                }}
              >
                <Plus className="mr-1 size-4" /> {packages.length === 1 ? t("mpsell.new.addStandardPackage") : t("mpsell.new.addPremiumPackage")}
              </Button>
            )}
          </div>
        )}

        {/* Step 4 — Media */}
        {step === 3 && (
          <div className="card-surface mt-6 space-y-6 p-6">
            <ImageCropDialog
              file={cropFile}
              open={cropOpen}
              onOpenChange={setCropOpen}
              round={false}
              aspect={cropKind === "cover" ? 16 / 9 : 1}
              onCropped={(f) => handleUpload(f, cropKind)}
            />
            <div>
              <Label>{t("mpsell.new.coverImage")}</Label>
              <p className="mb-2 text-xs text-muted-foreground">{t("mpsell.new.coverHint")}</p>
              {coverUrl ? (
                <div className="relative w-full max-w-sm overflow-hidden rounded-xl border border-border">
                  <img
                    src={previews[coverUrl] ?? coverUrl}
                    alt="Service cover"
                    className="aspect-video w-full object-cover"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute right-2 top-2"
                    onClick={() => setCoverUrl("")}
                  >
                    {t("mpsell.new.remove")}
                  </Button>
                </div>
              ) : (
                <label className="flex aspect-video w-full max-w-sm cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border text-sm text-muted-foreground hover:border-primary hover:text-primary">
                  {uploading === "cover" ? (
                    <Loader2 className="size-6 animate-spin" />
                  ) : (
                    <ImagePlus className="size-6" />
                  )}
                  {t("mpsell.new.uploadCover")}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      pickImage(e.target.files, "cover");
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
            </div>

            <div>
              <Label>{t("mpsell.new.galleryUpTo6")}</Label>
              <p className="mb-2 text-xs text-muted-foreground">{t("mpsell.new.galleryHint")}</p>
              <div className="grid grid-cols-3 gap-3">
                {gallery.map((url) => (
                  <div key={url} className="relative overflow-hidden rounded-lg border border-border">
                    <img
                      src={previews[url] ?? url}
                      alt="Work sample"
                      className="aspect-square w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setGallery((p) => p.filter((x) => x !== url))}
                      className="absolute right-1 top-1 rounded-full bg-background/90 p-1"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                ))}
                {gallery.length < 6 && (
                  <label className="flex aspect-square cursor-pointer items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary">
                    {uploading === "gallery" ? (
                      <Loader2 className="size-5 animate-spin" />
                    ) : (
                      <ImagePlus className="size-5" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        pickImage(e.target.files, "gallery");
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 5 — Requirements */}
        {step === 4 && (
          <div className="card-surface mt-6 space-y-4 p-6">
            <div>
              <Label>{t("mpsell.new.buyerRequirements")}</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("mpsell.new.buyerRequirementsHint")}
              </p>
            </div>
            <div className="space-y-2">
              {requirements.map((r, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={r}
                    onChange={(e) =>
                      setRequirements((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))
                    }
                    placeholder={suggestions.requirements[i] ?? t("mpsell.new.requirementPlaceholder")}
                  />
                  {requirements.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setRequirements((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setRequirements((p) => [...p, ""])}>
                  <Plus className="mr-1 size-4" /> {t("mpsell.new.addRequirement")}
                </Button>
                {suggestions.requirements
                  .filter((s) => !requirements.includes(s))
                  .map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setRequirements((p) => [...p.filter((x) => x.trim()), s])}
                      className="rounded-full border border-dashed border-border px-3 py-1 text-[11px] text-muted-foreground hover:border-primary hover:text-primary"
                    >
                      + {s}
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {step > 0 && (
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>
              {t("mpsell.new.back")}
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button variant="hot" disabled={!canContinue()} onClick={() => setStep((s) => s + 1)}>
              {t("mpsell.new.continue")}
            </Button>
          ) : (
            <Button variant="hot" disabled={create.isPending} onClick={() => create.mutate(true)}>
              {create.isPending ? t("mpsell.new.saving") : t("mpsell.new.submitForReview")}
            </Button>
          )}
          <Button
            variant="outline"
            disabled={!title || !parentId || create.isPending}
            onClick={() => create.mutate(false)}
          >
            {t("mpsell.new.saveDraft")}
          </Button>
        </div>
      </div>
    </MarketplaceGate>
  );
}
